import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ClipboardCheck, FileWarning, LogOut, Scale } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { publicApi } from "@/lib/api";

const iconMap = {
  "verfahrensordnung-aufnahme": ClipboardCheck,
  "verfahrensordnung-beschwerde": Scale,
  "verfahrensordnung-austritt": LogOut,
  "verfahrensordnung-entzug": FileWarning,
};

export default function ProcedurePage({ session, onLogout }) {
  const [content, setContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      const data = await publicApi.getContent();
      setContent(data);
    };
    load();
  }, []);

  const docs = useMemo(
    () => (content?.legal || []).filter((doc) => doc.slug.startsWith("verfahrensordnung-")).sort((a, b) => (a.display_order || 999) - (b.display_order || 999)),
    [content],
  );

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Verfahrensordnung"
      pageDescription="Verfahrensordnung zu Aufnahme, Beschwerde, Austritt und Entzug bei REEXPERTS."
    >
      <div className="bg-[#111111] text-[#f7f1e8]">
        <div className="mx-auto flex max-w-7xl flex-col gap-12 px-6 py-14 lg:px-8 lg:py-20">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="procedure-hero-section">
            <div className="space-y-6 rounded-[2rem] border border-[#d4af37]/25 bg-[#141414] p-8">
              <Badge className="w-fit rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]" data-testid="procedure-hero-badge">
                <span className="mr-3 inline-flex items-center gap-1">
                  <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                  <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                  <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
                </span>
                Operative Verfahrensordnung
              </Badge>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#b99d6b]" data-testid="procedure-hero-kicker">Aufnahme · Beschwerde · Austritt · Entzug</p>
                <h1 className="mt-4 font-serif text-4xl leading-[1.04] text-white lg:text-5xl" data-testid="procedure-hero-heading">
                  Die operative Ordnung der wesentlichen Verbandsverfahren.
                </h1>
              </div>
              <p className="text-base leading-relaxed text-[#cbc2b7]" data-testid="procedure-hero-description">
                Nachstehend werden die maßgeblichen Verfahrenswege der Vereinigung in geordneter Form dargestellt. Die Ordnung umfasst insbesondere Aufnahme, Beschwerdebearbeitung, Austritt sowie Entzug oder Ausschluss und dient der administrativen Klarheit innerhalb der Verbandsstruktur.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="procedure-hero-apply-link">
                  <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]">Aufnahmeverfahren beginnen</Button>
                </Link>
                <Link to="/rechtliches" data-testid="procedure-hero-legal-link">
                  <Button variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5">Zur Rechtsseite</Button>
                </Link>
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {docs.map((doc) => {
                const Icon = iconMap[doc.slug] || FileWarning;
                return (
                  <Card key={doc.slug} className="border-white/10 bg-[#171717]" data-testid={`procedure-summary-card-${doc.slug}`}>
                    <CardContent className="space-y-4 p-6">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="font-semibold text-white">{doc.title}</p>
                      <p className="text-sm leading-relaxed text-[#c7bfb3]">{doc.content.split("\n\n")[0]}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.section>

          <section className="grid gap-8 xl:grid-cols-[260px_1fr]" data-testid="procedure-content-section">
            <aside className="rounded-[2rem] border border-white/10 bg-[#151515] p-6" data-testid="procedure-sidebar">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#b99d6b]" data-testid="procedure-sidebar-kicker">Verfahrensgliederung</p>
              <div className="mt-5 space-y-2">
                {docs.map((doc) => (
                  <a
                    key={doc.slug}
                    href={`#${doc.slug}`}
                    className="block rounded-2xl border border-white/10 px-4 py-3 text-sm text-[#e8e1d5] transition-colors hover:border-[#d4af37]/30 hover:bg-[#e5d3b3]/10"
                    data-testid={`procedure-sidebar-link-${doc.slug}`}
                  >
                    {doc.title}
                  </a>
                ))}
              </div>
            </aside>

            <div className="space-y-5">
              {docs.map((doc) => {
                const Icon = iconMap[doc.slug] || FileWarning;
                return (
                  <Card key={doc.slug} id={doc.slug} className="scroll-mt-28 border-white/10 bg-[#171717]" data-testid={`procedure-document-card-${doc.slug}`}>
                    <CardContent className="space-y-4 p-8">
                      <div className="flex items-start gap-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#b99d6b]" data-testid={`procedure-document-kicker-${doc.slug}`}>Ordnung</p>
                          <h2 className="mt-2 font-serif text-3xl text-white" data-testid={`procedure-document-title-${doc.slug}`}>{doc.title}</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-testid={`procedure-document-flag-${doc.slug}`}>
                        <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                        <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                        <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
                      </div>
                      <p className="whitespace-pre-line text-sm leading-8 text-[#ded6ca]" data-testid={`procedure-document-content-${doc.slug}`}>
                        {doc.content}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
